import clsx from 'clsx';
import some from 'lodash/some';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { toggleSampleBasketMedia } from 'Apollo/Store/Showroom/UserView/Samples/Basket/resolvers';
import { closeCurrentMediaMobileMenu } from 'Apollo/Store/Showroom/UserView/resolvers';
import {
	ShowroomMobileMenuType,
	useGetMobileMenuOpenedQuery,
	useGetShowroomMediaMobileInfoQuery,
	useGetShowroomSamplesBasketQuery
} from 'generated/graphql';

import { useTimeout } from 'lib/Hooks';
import { useDownloadSingleMedia } from 'lib/ShowroomSingleMediaDownload';
import { useGetCurrentShowroomData, useIsShowroomBasketEnabled, useIsShowroomDowloadOptionEnabled } from 'lib/showroom';

import CircularProgress from '@material-ui/core/CircularProgress';
import Collapse from '@material-ui/core/Collapse';
import Divider from '@material-ui/core/Divider';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import DownloadIcon from '@material-ui/icons/SaveAlt';

import { ReactComponent as AddToSamplesBasketIcon } from 'icons/addToSamplesBasket.svg';
import { ReactComponent as RemoveFromSamplesBasketIcon } from 'icons/removeFromSamplesBasket.svg';

import ShowroomMobileActionsMenuLayout from '../MobileActionsMenuLayout';
import { useStyles } from './styles';

const ShowroomMobileMediaMenu = () => {
	const classes = useStyles();
	const { t } = useTranslation();

	const showroom = useGetCurrentShowroomData();
	const basketEnabled = useIsShowroomBasketEnabled();
	const downloadEnabled = useIsShowroomDowloadOptionEnabled();
	const {
		data: {
			showroom: {
				userView: { activeMediaMobileMenu }
			}
		}
	} = useGetMobileMenuOpenedQuery();
	const { data } = useGetShowroomMediaMobileInfoQuery({
		variables: {
			mediaId: activeMediaMobileMenu?.mediaId
		},
		skip: !activeMediaMobileMenu?.mediaId
	});

	const isSamplesMedia = data?.cachedMedia.isSample;
	const { data: samplesBasketData } = useGetShowroomSamplesBasketQuery({
		variables: { showroomId: showroom.id },
		skip: !isSamplesMedia
	});

	const isSampleAdded = React.useMemo(
		() => isSamplesMedia && some(samplesBasketData?.showroomSamplesBasket, { id: data?.cachedMedia.id }),
		[isSamplesMedia, samplesBasketData?.showroomSamplesBasket, data?.cachedMedia.id]
	);

	const [qualityOptions, downloadMedia, { loading: downloadMediaLoading }] = useDownloadSingleMedia(data?.cachedMedia, showroom as any);

	const displayDownload = downloadEnabled && !!data?.cachedMedia;
	const displayBasket = basketEnabled && isSamplesMedia;
	const actionsAvailable = displayDownload || displayBasket;

	const [downloadListOpen, setDownloadListOpen] = React.useState(false);
	const [open, setOpen] = React.useState(false);

	const closeMobileMenu = React.useCallback(() => {
		setDownloadListOpen(false);
		closeCurrentMediaMobileMenu();
	}, []);

	const { run, clear } = useTimeout();

	// If the menu is already open, this useEffect simulates a reopen action
	React.useEffect(() => {
		setDownloadListOpen(false);
		if (activeMediaMobileMenu?.type !== ShowroomMobileMenuType.Section || !actionsAvailable) return setOpen(false);
		if (open) {
			setOpen(false);
			run(() => {
				setOpen(true);
			}, 200);
		} else setOpen(true);
		return () => {
			clear();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [activeMediaMobileMenu, actionsAvailable, clear, run]);

	return (
		<ShowroomMobileActionsMenuLayout open={open} onClose={closeMobileMenu}>
			<List disablePadding>
				{displayDownload && (
					<>
						<ListItem
							id="showroomMobileMediaMenuSingleDownloadOption"
							className={classes.menuListItem}
							button
							onClick={downloadMediaLoading ? null : () => setDownloadListOpen(state => !state)}
							disableRipple
						>
							{downloadMediaLoading ? (
								<CircularProgress className={classes.loader} size={20} />
							) : (
								<ListItemIcon>
									<DownloadIcon className={classes.listItemIcon} />
								</ListItemIcon>
							)}
							<ListItemText
								primary={t(`showroom.download.item.${data?.cachedMedia.mediaType.toLowerCase()}`)}
								classes={{ primary: classes.listItemText }}
							/>
						</ListItem>
						<Collapse in={downloadListOpen} unmountOnExit className={classes.subListCollapse}>
							<List component="div" className={classes.subList} disablePadding>
								{qualityOptions
									.map(({ label, value }, index) => [
										<ListItem
											id={`showroomMobileMediaSingleDownloadOptionItem_${value}`}
											key={`showroomMobileMediaSingleDownloadOptionItem_${value}`}
											className={clsx(classes.menuListItem, classes.subListItem)}
											button
											onClick={() => {
												downloadMedia(value);
												setDownloadListOpen(false);
											}}
											disableRipple
										>
											{label}
										</ListItem>,
										index !== qualityOptions.length - 1 ? (
											<Divider key={`showroomMobileMediaSingleDownloadOptionItem_${value}_divider`} variant="middle"></Divider>
										) : null
									])
									.map(el => el)}
							</List>
						</Collapse>
					</>
				)}
				{displayBasket && (
					<ListItem
						id="showroomMobileMediaMenuAddBasketOption"
						className={classes.menuListItem}
						button
						onClick={() => {
							toggleSampleBasketMedia(showroom.tenant.uuid, showroom.id, data?.cachedMedia.id);
							closeMobileMenu();
						}}
						disableRipple
					>
						<ListItemIcon>{isSampleAdded ? <RemoveFromSamplesBasketIcon /> : <AddToSamplesBasketIcon />}</ListItemIcon>
						<ListItemText
							primary={isSampleAdded ? t('showroom.samples.list.remove') : t('showroom.samples.list.add')}
							classes={{ primary: classes.listItemText }}
						/>
					</ListItem>
				)}
			</List>
		</ShowroomMobileActionsMenuLayout>
	);
};

export default React.memo(ShowroomMobileMediaMenu);
